"use client";

import React, { useEffect, useRef, useState } from "react";
import { Users, Briefcase, Wifi, Coffee, ChevronRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.1,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) observer.unobserve(entry.target);
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const vehicles = [{
  id: "executive-suv",
  name: "Executive SUV",
  model: "CHEVROLET SUBURBAN 2023",
  shortDesc: "The ultimate American SUV for group executive travel. Spacious, powerful, and built for Bay Area comfort.",
  image: "https://just-cheetah.10web.cloud/wp-content/uploads/2026/04/image_4vhgnrz7.png",
  image360: "https://just-cheetah.10web.cloud/wp-content/uploads/2026/04/23-Chevrolet-Suburban-Cargo-Space.webp",
  passengers: "Up to 6",
  luggage: "6 Large Bags",
  wifi: "Secure 5G Hotspot",
  refreshments: "Premium Mineral Water"
}, {
  id: "denali-suv",
  name: "Premium SUV",
  model: "GMC YUKON DENALI 2023",
  shortDesc: "The Denali standard — premium interior, smooth ride, executive‑class refinement for demanding clients.",
  image: "https://just-cheetah.10web.cloud/wp-content/uploads/2026/04/image-1.png",
  image360: "https://just-cheetah.10web.cloud/wp-content/uploads/2026/04/denaly.avif",
  passengers: "Up to 6",
  luggage: "5 Large Bags",
  wifi: "Secure 5G Hotspot",
  refreshments: "Premium Mineral Water"
}, {
  id: "elite-sedan",
  name: "Elite Sedan",
  model: "CADILLAC VISTIQ 2026",
  shortDesc: "The pinnacle of American luxury. Whisper‑quiet and impeccably appointed for the discerning executive.",
  image: "https://just-cheetah.10web.cloud/wp-content/uploads/2026/04/cadillac-vistiq-out.jpg",
  image360: "https://just-cheetah.10web.cloud/wp-content/uploads/2026/04/IMAGE-2-Turnersville_Cadillac_Vistiq_2026.avif",
  passengers: "Up to 2 VIP",
  luggage: "3 Standard Bags",
  wifi: "Secure 5G Hotspot",
  refreshments: "Premium Mineral Water"
}];
export default function TheExecutiveCollection() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="150227" id="the-elite-fleet" className="dark bg-background text-foreground py-24 px-6 lg:px-12 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        
        <div ref={headerRef} className={`text-center mb-16 transition-all duration-1000 transform ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl mb-6 text-foreground tracking-tight">
            The Elite Fleet
          </h2>
          <p className="font-sans text-muted-foreground max-w-2xl mx-auto text-lg leading-relaxed">
            A curated selection of our midnight executive vehicles. Select a masterpiece to explore its high-performance specifications and bespoke interior.
          </p>
        </div>

        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
          {vehicles.map((vehicle, i) => <VehicleCard key={vehicle.id} vehicle={vehicle} index={i} />)}
        </div>
      </div>
    </section>;
}
function VehicleCard({
  vehicle,
  index
}: {
  vehicle: typeof vehicles[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <div data-index={index} ref={ref} className={`transition-all duration-1000 transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-16"}`} style={{
    transitionDelay: `${index * 150}ms`
  }}>
      <Dialog>
        <DialogTrigger asChild>
          <div className="cursor-pointer group h-full">
            <Card className="bg-card border-border/20 overflow-hidden transition-all duration-500 hover:border-primary/50 hover:shadow-2xl hover:shadow-primary/10 h-full flex flex-col rounded-none">
              <div className="relative h-72 overflow-hidden bg-muted/10">
                <img src={vehicle.image} alt={vehicle.name} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105 opacity-80 group-hover:opacity-100" />
                <div className="absolute inset-0 bg-gradient-to-t from-background/95 via-background/20 to-transparent" />
              </div>
              
              <CardHeader className="relative z-10 -mt-16 pb-4 px-8">
                <CardTitle className="font-serif text-3xl text-foreground mb-1">
                  {vehicle.name}
                </CardTitle>
                <CardDescription className="font-mono text-primary tracking-widest uppercase text-xs font-semibold">
                  {vehicle.model}
                </CardDescription>
              </CardHeader>
              
              <CardContent className="flex-grow px-8">
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {vehicle.shortDesc}
                </p>
              </CardContent>
              
              <CardFooter className="px-8 pb-8 pt-4">
                <span className="text-primary text-sm font-medium flex items-center gap-2 group-hover:gap-4 transition-all duration-300 uppercase tracking-widest">
                  Explore Interior <ChevronRight className="w-4 h-4" />
                </span>
              </CardFooter>
            </Card>
          </div>
        </DialogTrigger>

        <DialogContent className="dark bg-card text-foreground border-border/30 sm:max-w-4xl p-0 overflow-hidden rounded-none shadow-2xl shadow-black/70">
          <div className="grid grid-cols-1 md:grid-cols-2 h-full max-h-[90vh] overflow-y-auto md:overflow-hidden">
            
            <div className="bg-muted/5 relative h-64 md:h-full min-h-[300px]">
              <img src={vehicle.image360} alt={`${vehicle.name} Interior View`} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-r from-transparent to-card/90 md:to-card/30" />
              <div className="absolute bottom-4 left-4 bg-background/90 backdrop-blur-md px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-primary border border-primary/30 font-mono">
                360-Degree Perspective
              </div>
            </div>

            
            <div className="p-8 md:p-12 flex flex-col justify-between bg-card">
              <div>
                <DialogHeader className="mb-8 text-left">
                  <DialogTitle className="font-serif text-4xl mb-2 text-foreground">
                    {vehicle.name}
                  </DialogTitle>
                  <DialogDescription className="text-primary text-sm uppercase tracking-[0.15em] font-semibold font-mono">
                    {vehicle.model}
                  </DialogDescription>
                </DialogHeader>

                <div className="mb-8">
                  <h4 className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-4 border-b border-border/30 pb-2">
                    Executive Specs
                  </h4>
                  <Table>
                    <TableBody>
                      <TableRow className="border-border/10 hover:bg-transparent">
                        <TableCell className="font-medium text-muted-foreground flex items-center gap-3 py-4 pl-0 text-xs uppercase tracking-wider">
                          <Users className="w-4 h-4 text-primary" /> Passengers
                        </TableCell>
                        <TableCell className="text-right text-foreground pr-0 font-medium">
                          {vehicle.passengers}
                        </TableCell>
                      </TableRow>
                      <TableRow className="border-border/10 hover:bg-transparent">
                        <TableCell className="font-medium text-muted-foreground flex items-center gap-3 py-4 pl-0 text-xs uppercase tracking-wider">
                          <Briefcase className="w-4 h-4 text-primary" /> Luggage
                        </TableCell>
                        <TableCell className="text-right text-foreground pr-0 font-medium">
                          {vehicle.luggage}
                        </TableCell>
                      </TableRow>
                      <TableRow className="border-border/10 hover:bg-transparent">
                        <TableCell className="font-medium text-muted-foreground flex items-center gap-3 py-4 pl-0 text-xs uppercase tracking-wider">
                          <Wifi className="w-4 h-4 text-primary" /> Digital
                        </TableCell>
                        <TableCell className="text-right text-foreground pr-0 font-medium">
                          {vehicle.wifi}
                        </TableCell>
                      </TableRow>
                      <TableRow className="border-border/10 hover:bg-transparent">
                        <TableCell className="font-medium text-muted-foreground flex items-center gap-3 py-4 pl-0 text-xs uppercase tracking-wider">
                          <Coffee className="w-4 h-4 text-primary" /> Cabin
                        </TableCell>
                        <TableCell className="text-right text-foreground pr-0 font-medium">
                          {vehicle.refreshments}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              </div>

              <DialogFooter className="sm:justify-start mt-auto pt-6">
                <Button className="w-full font-sans tracking-[0.2em] uppercase text-xs py-7 rounded-none bg-primary text-primary-foreground hover:bg-primary/80 transition-all duration-300 shadow-lg shadow-primary/20" asChild>
                  <Link to="/ReachUsPage">Reserve Vehicle</Link>
                </Button>
              </DialogFooter>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>;
}